import {
  drinkingWindowBand,
  type DrinkingWindowSources,
  type DrinkingWindowYears,
  type WindowSource,
} from "@corklog/shared";
import { useTranslation } from "react-i18next";

/**
 * The drinking window as a band: the years before a wine is ready, the plateau
 * where it is at its best, the years after, and where now falls on it.
 *
 * This is the only loud element in the interface, per design-direction.md, and
 * everything around it stays quiet so it can carry the page. It is drawn with
 * percentage widths rather than an SVG viewBox: a band that stretches has to
 * keep its hairlines at one pixel and its now-marker un-squashed, and
 * preserveAspectRatio="none" would distort both.
 *
 * No state is carried by colour alone. Each band is labelled in words for a
 * screen reader, and the row beneath it names the state in text — which is
 * also what makes the interface readable to someone who simply has the
 * brightness down in a dark cellar.
 */

const SEGMENT_COLOUR = {
  young: "bg-young",
  ready: "bg-ready",
  peak: "bg-peak",
  past: "bg-past",
} as const;

/** One source and the years that came from it, in the order the years occur. */
type Provenance = { source: WindowSource; years: number[] };

/**
 * Group the four years by where each came from.
 *
 * Grouped rather than listed one by one because the four years of a real wine
 * almost always share a source — 95 of the 121 wines in this cellar carry one
 * year, all but one of them from the import — and *drickbar från: importen,
 * höjdpunkt från: importen, …* is a table of the same word repeated. Where they
 * genuinely differ, the years say which is which.
 *
 * A year whose source is missing contributes nothing rather than a guess: the
 * two are written and cleared together, and inventing a provenance is the one
 * thing recording it was meant to stop.
 */
function provenanceOf(
  years: DrinkingWindowYears,
  sources: DrinkingWindowSources,
): Provenance[] {
  const pairs: readonly (readonly [number | null, WindowSource | null])[] = [
    [years.drinkFrom, sources.drinkFromSource],
    [years.peakFrom, sources.peakFromSource],
    [years.peakTo, sources.peakToSource],
    [years.drinkBy, sources.drinkBySource],
  ];

  const groups: Provenance[] = [];
  for (const [year, source] of pairs) {
    if (year === null || source === null) continue;
    const found = groups.find((group) => group.source === source);
    if (found === undefined) groups.push({ source, years: [year] });
    else found.years.push(year);
  }
  return groups;
}

/** A year written under the band, and where along it, 0 to 1. */
export type BandYear = { readonly year: number; readonly at: number };

/**
 * How close two years may stand under the band before the second is left out,
 * as a share of its width. A year is four figures at the size of a caption,
 * about thirty pixels; a phone's band is about three hundred, so a tenth of it
 * plus a little air is what keeps two from touching.
 */
const YEAR_GAP = 0.13;

/**
 * The years a wine's window was given, placed where they fall on its band.
 * Asked for by the owner on 2026-10-07, for the wine list.
 *
 * **Only the recorded years**, not the band's ends: the band always stretches
 * to take in this year, so its left end is often today, which is a fact about
 * the calendar rather than about the wine — and *now* is already marked above.
 * A year given twice — a plateau one year long, or a peak that runs to the
 * last drinkable year — is written once.
 *
 * **When two stand too close, the later one wins**, and the earlier is left
 * out rather than squeezed or overlapped. The end of the window is the year a
 * list is read for — it is the one the spreadsheet carried, and the one that
 * decides what to open — so it is always written; the band itself still shows
 * the stretch whose start was left out, and the wine's page writes every year
 * in full.
 */
export function bandYears(
  years: DrinkingWindowYears,
  band: { firstYear: number; lastYear: number },
): BandYear[] {
  const span = band.lastYear - band.firstYear;
  const known = [
    ...new Set(
      [years.drinkFrom, years.peakFrom, years.peakTo, years.drinkBy].filter(
        (year): year is number => year !== null,
      ),
    ),
  ].sort((a, b) => a - b);

  const kept: BandYear[] = [];
  // From the right, so the latest year is placed first and the ones before it
  // give way to it.
  for (const year of [...known].reverse()) {
    const at = (year - band.firstYear) / span;
    const nearest = kept[kept.length - 1];
    if (nearest === undefined || nearest.at - at >= YEAR_GAP) kept.push({ year, at });
  }
  return kept.reverse();
}

/** Keeps a label on the band at its ends, where a centred one would hang off
 *  the row — the rule the *nu* marker above follows. */
function shiftFor(at: number): string {
  return at < 0.08
    ? "translateX(0)"
    : at > 0.92
      ? "translateX(-100%)"
      : "translateX(-50%)";
}

export function DrinkingWindow({
  years,
  sources,
  currentYear,
  yearsOnBand = false,
}: {
  years: DrinkingWindowYears;
  /**
   * Where the years came from, where the caller has it. Optional because the
   * list row deliberately does not show it: provenance is read when a single
   * wine is being judged, and a line of it under every band would be a column
   * of the word *importen* down a page whose one loud element is the band.
   */
  sources?: DrinkingWindowSources;
  currentYear: number;
  /**
   * Whether the wine's years are written under the band, where they fall.
   * On a list row, where the band is all there is of the window; a wine's own
   * page writes the years out in full beside it and does not need them twice.
   * The band's span is then left off the line below, since its far end is
   * written on the band and its near end is usually just this year.
   */
  yearsOnBand?: boolean;
}) {
  const { t } = useTranslation();
  const band = drinkingWindowBand(years, currentYear);

  if (band === null) {
    return <p className="text-muted mt-3 text-sm">{t("window.unknown")}</p>;
  }

  const state = t(`window.state.${band.state}`);
  const description = t("window.description", {
    state,
    from: band.firstYear,
    to: band.lastYear,
  });

  const provenance = sources === undefined ? [] : provenanceOf(years, sources);
  const [onlyGroup] = provenance;

  // Keep the marker's label on the band at the extremes: a wine whose window
  // has not opened has now at the very left, and a centred label would hang
  // off the edge of the row.
  const labelShift = shiftFor(band.nowAt);
  const written = yearsOnBand ? bandYears(years, band) : [];

  return (
    <div className="mt-3">
      {/* Now, above the band: the tick points at the year, the word says what
          the tick is. Design-direction.md draws this as "▲ nu". */}
      <div className="relative h-4">
        <div
          className="absolute bottom-0 whitespace-nowrap"
          style={{ left: `${band.nowAt * 100}%`, transform: labelShift }}
        >
          <span className="text-ink font-display text-xs">{t("window.now")}</span>
        </div>
      </div>

      <div className="relative" role="img" aria-label={description}>
        <div className="flex h-3 w-full overflow-hidden">
          {band.segments.map((segment) => (
            <div
              key={`${segment.state}-${segment.fromYear}`}
              className={SEGMENT_COLOUR[segment.state]}
              style={{ width: `${(segment.end - segment.start) * 100}%` }}
            />
          ))}
        </div>

        {/* The tick itself, drawn over the band and a little above it so it
            reads as a position on the band rather than a gap in it. */}
        <div
          className="bg-ink pointer-events-none absolute -top-1 h-5 w-0.5"
          style={{ left: `${band.nowAt * 100}%`, transform: "translateX(-50%)" }}
          aria-hidden="true"
        />
      </div>

      {/* The years, under the band where they fall: quiet figures in the
          display face, tabular so they read as one scale — the band is the
          loud thing, and these only say where its edges are. */}
      {written.length > 0 && (
        <div className="relative mt-1 h-4" data-band-years="">
          {written.map((label) => (
            <span
              key={label.year}
              className="text-muted font-display absolute top-0 text-xs whitespace-nowrap tabular-nums"
              style={{ left: `${label.at * 100}%`, transform: shiftFor(label.at) }}
            >
              {label.year}
            </span>
          ))}
        </div>
      )}

      <div
        className={`${written.length > 0 ? "mt-1" : "mt-2"} flex items-baseline justify-between gap-4 text-sm`}
      >
        <span
          className={
            band.state === "peak"
              ? "text-peak font-display"
              : band.state === "past"
                ? "text-past font-display"
                : "text-ink font-display"
          }
        >
          {state}
        </span>
        {!yearsOnBand && (
          <span className="text-muted text-xs">
            {band.firstYear}–{band.lastYear}
          </span>
        )}
      </div>

      {/* Where the band came from, in a word, under the band it qualifies.
          The band itself is the loud element and this is the quietest text on
          the page, because it is read once — when deciding how much the years
          above are worth — and not every time the page is opened. */}
      {provenance.length > 0 && (
        <p className="text-muted mt-1 text-xs">
          {provenance.length === 1 && onlyGroup !== undefined
            ? t(`window.source.${onlyGroup.source}`, { count: onlyGroup.years.length })
            : provenance
                .map((group) =>
                  t(`window.source.years.${group.source}`, {
                    years: group.years.join(", "),
                  }),
                )
                .join(" · ")}
        </p>
      )}
    </div>
  );
}
